import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import type { Order } from '@shared/types';

const STATUS_COLORS: Record<string, string> = {
  completed: '#34d399',
  pending: '#a1a1aa',
  failed: '#f87171',
  expired: '#fbbf24',
  on_hold: '#fb923c',
  refunded: '#a78bfa',
  payment_initiated: '#38bdf8',
  usdc_received: '#38bdf8',
  forwarding: '#38bdf8',
};

interface Props {
  orders: Order[];
}

export default function StatusDistribution({ orders }: Props) {
  if (orders.length === 0) return null;

  const counts: Record<string, number> = {};
  for (const o of orders) {
    counts[o.status] = (counts[o.status] || 0) + 1;
  }

  const data = Object.entries(counts)
    .map(([name, value]) => ({ name: name.replace(/_/g, ' '), value }))
    .sort((a, b) => b.value - a.value);

  return (
    <div
      className="rounded p-5"
      style={{
        border: '1px solid var(--dash-border)',
        background: 'var(--dash-card)',
      }}
    >
      <div
        className="font-mono text-[10px] uppercase tracking-widest mb-4"
        style={{ color: 'var(--dash-text-dim)' }}
      >
        Status Distribution
      </div>
      <div className="flex items-center gap-6">
        <div className="w-36 h-36">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={30}
                outerRadius={55}
                strokeWidth={0}
              >
                {data.map((entry, i) => (
                  <Cell
                    key={i}
                    fill={STATUS_COLORS[entry.name.replace(/ /g, '_')] || '#52525b'}
                  />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  background: 'var(--dash-overlay)',
                  border: '1px solid var(--dash-border-solid)',
                  borderRadius: '4px',
                  fontFamily: '"IBM Plex Mono"',
                  fontSize: 12,
                  color: 'var(--dash-text-secondary)',
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="space-y-1.5">
          {data.map((entry) => (
            <div key={entry.name} className="flex items-center gap-2">
              <div
                className="w-2 h-2 rounded-full"
                style={{ background: STATUS_COLORS[entry.name.replace(/ /g, '_')] || '#52525b' }}
              />
              <span
                className="font-sans text-xs capitalize"
                style={{ color: 'var(--dash-text-muted)' }}
              >
                {entry.name}
              </span>
              <span
                className="font-mono text-xs"
                style={{ color: 'var(--dash-text-tertiary)' }}
              >
                {entry.value}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
